<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { api } from '@/api/api.js'
import { useScrollLock } from '@/composables/useScrollLock'
import { useGeolocation } from '@/composables/useGeolocation'
import { useFileUpload } from '@/composables/useFileUpload'
import { usePhoneInput } from '@/composables/usePhoneInput'

const props = defineProps<{ selectedTariff?: string }>()
const emit = defineEmits<{ close: [] }>()

const route = useRoute()
const currentPage = ref(route.fullPath)
const currentStep = ref(1)
const isQuickCall = ref(false)
const isSubmitted = ref(false)
const name = ref('')
const contactMethod = ref('')

const { lock } = useScrollLock()
const { region, detectedCity, detect: detectRegion } = useGeolocation()
const { fileName, uploadedFileId, isDragging, clearFile, handleDrop, triggerFileUpload } =
  useFileUpload()
const { phone, handleInput: handlePhoneInput } = usePhoneInput()
const { phone: quickCallPhone, handleInput: handleQuickPhoneInput } = usePhoneInput()

const contactPhoneRef = ref<HTMLInputElement | null>(null)
const quickCallPhoneRef = ref<HTMLInputElement | null>(null)

const focusPhoneInput = (input: HTMLInputElement | null) => {
  if (!input) return
  input.focus()
  input.setSelectionRange(2, 2)
}

onMounted(async () => {
  lock()
  await detectRegion()
  if (isQuickCall.value) focusPhoneInput(quickCallPhoneRef.value)
})

const regions = ['Москва и МО', 'Санкт-Петербург и ЛО', 'Другой регион']

const contactMethods = [
  { value: 'telegram', label: 'Telegram' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'call', label: 'Звонок' },
]

const nextStep = () => {
  if (currentStep.value === 2 && !region.value) return
  if (currentStep.value < 3) {
    currentStep.value++
    if (currentStep.value === 3) {
      setTimeout(() => focusPhoneInput(contactPhoneRef.value), 0)
    }
  }
}

const prevStep = () => {
  if (currentStep.value > 1) currentStep.value--
}

const showQuickCall = () => {
  isQuickCall.value = true
  currentStep.value = 4
  setTimeout(() => focusPhoneInput(quickCallPhoneRef.value), 0)
}

const handleSubmit = async () => {
  if (!isQuickCall.value && currentStep.value === 3) {
    if (!name.value || !phone.value || !contactMethod.value) return
  }
  if (isQuickCall.value && !quickCallPhone.value) return

  const formData: Record<string, unknown> = {
    name: name.value,
    phone: isQuickCall.value ? quickCallPhone.value : phone.value,
    region: isQuickCall.value ? `${region.value} (исходя из IP)` : region.value,
    page: currentPage.value,
    tariff: props.selectedTariff ?? null,
    file_id: uploadedFileId.value,
    contact_method: contactMethod.value,
    is_quick_call: isQuickCall.value,
  }

  try {
    await api.addApplication(formData)
    isSubmitted.value = true
    setTimeout(() => emit('close'), 3000)
  } catch (error) {
    console.error('Ошибка при отправке заявки:', error)
  }
}

const closeModal = () => {
  emit('close')
}

defineExpose({
  submitForm: handleSubmit,
  handlePhoneInput,
  region,
  detectedRegion: detectedCity,
  name,
  phone,
  contactMethod,
  currentStep,
  fileName,
  isDragging,
  isQuickCall,
  isSubmitted,
})
</script>

<template>
  <div class="modal-overlay" @click.self="closeModal">
    <div class="modal-content">
      <div class="modal-body">
        <div class="header-container">
          <div v-if="currentStep < 4" class="progress-bar">
            <div
              class="progress"
              :style="{
                width: currentStep === 1 ? '33.33%' : currentStep === 2 ? '66.66%' : '100%',
              }"
            ></div>
            <div class="progress-text" :style="{ color: currentStep === 1 ? 'black' : 'white' }">
              {{ currentStep }}/3
            </div>
          </div>
          <div class="close-button" @click="closeModal">
            <span></span>
            <span></span>
          </div>
        </div>

        <div v-if="isSubmitted" class="success-message">
          <div class="success-icon">✓</div>
          <h3>Успешно отправлено</h3>
          <p>Скоро свяжемся с вами</p>
        </div>

        <form v-else class="contact-form" @submit.prevent="handleSubmit">
          <div class="form-content">
            <div v-if="currentStep === 1 && !isQuickCall" class="form-step">
              <h3>Если у вас есть ТЗ, прикрепите его</h3>
              <div
                class="file-upload"
                :class="{ dragging: isDragging }"
                @dragover.prevent="isDragging = true"
                @dragleave.prevent="isDragging = false"
                @drop.prevent="handleDrop"
                @click="fileName ? undefined : triggerFileUpload()"
              >
                <p v-if="isDragging">Можно отпускать</p>
                <p v-else-if="!fileName">Перетащите файл сюда или кликните для выбора</p>
                <p v-if="fileName">
                  {{ fileName }}
                  <button type="button" @click="clearFile">Отменить</button>
                </p>
              </div>
            </div>

            <div v-if="currentStep === 2 && !isQuickCall" class="form-step">
              <h3>Ваш город?</h3>
              <div class="form-field">
                <select v-model="region" required>
                  <option v-for="r in regions" :key="r" :value="r">{{ r }}</option>
                </select>
              </div>
            </div>

            <div v-if="currentStep === 3 && !isQuickCall" class="form-step">
              <div class="form-field">
                <input type="text" v-model="name" required placeholder=" " />
                <span class="placeholder">Имя</span>
              </div>
              <div class="form-field">
                <input
                  ref="contactPhoneRef"
                  type="tel"
                  :value="phone"
                  @input="handlePhoneInput"
                  required
                  placeholder=" "
                />
                <span class="placeholder">Телефон</span>
              </div>
              <div class="form-field">
                <select v-model="contactMethod" required>
                  <option value="" disabled selected>Способ связи</option>
                  <option
                    v-for="method in contactMethods"
                    :key="method.value"
                    :value="method.value"
                  >
                    {{ method.label }}
                  </option>
                </select>
              </div>
            </div>

            <div v-if="currentStep === 4 || isQuickCall" class="form-step">
              <h3>Введите номер телефона для звонка</h3>
              <div class="form-field">
                <input
                  ref="quickCallPhoneRef"
                  type="tel"
                  :value="quickCallPhone"
                  @input="handleQuickPhoneInput"
                  required
                  placeholder=" "
                />
                <span class="placeholder">Телефон</span>
              </div>
            </div>
          </div>

          <div class="form-navigation">
            <button
              type="button"
              v-if="currentStep > 1 && !isQuickCall"
              class="nav-button"
              @click="prevStep"
            >
              Назад
            </button>

            <button
              v-if="currentStep < 3 && !isQuickCall"
              type="button"
              :class="
                currentStep === 1 ? (fileName ? 'next-button' : 'disabled-button') : 'next-button'
              "
              @click="nextStep"
            >
              <span v-if="currentStep === 1 && !fileName">Не хочу, дальше</span>
              <span v-else>Вперед</span>
            </button>

            <button
              type="submit"
              v-if="(currentStep === 3 && !isQuickCall) || currentStep === 4 || isQuickCall"
              class="submit-button"
            >
              Отправить
            </button>
          </div>

          <div class="quick-call-button-container" v-if="!isQuickCall">
            <button type="button" class="quick-call-button" @click="showQuickCall">
              Быстрая заявка
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background: var(--color-bg-primary);
  width: 100%;
  max-width: 31.25rem;
  padding: 2.5rem;
  position: relative;
  margin: 1.25rem;
}

.close-button {
  width: 1.125rem;
  height: 1.125rem;
  position: relative;
  cursor: pointer;
}

.close-button span {
  position: absolute;
  width: 100%;
  height: 0.063rem;
  background-color: var(--color-text-secondary);
  top: 50%;
}

.close-button span:first-child {
  transform: rotate(45deg);
}

.close-button span:last-child {
  transform: rotate(-45deg);
}

.contact-form {
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 0;
  height: 100%;
}

.form-field {
  position: relative;
}

.form-field input,
.form-field select {
  width: 100%;
  padding: 0.938rem;
  border: 0.063rem solid rgba(0, 0, 0, 0.51);
  background: var(--color-bg-primary);
  font-size: 0.875rem;
  transition: all 0.3s ease;
  appearance: none;
  box-sizing: border-box;
}

.form-field select {
  cursor: pointer;
}

.placeholder {
  position: absolute;
  left: 0.938rem;
  top: 50%;
  transform: translateY(-50%);
  color: rgba(0, 0, 0, 0.7);
  font-size: 0.875rem;
  transition: all 0.3s ease;
  pointer-events: none;
  background: var(--color-bg-primary);
  padding: 0 0.313rem;
}

.form-field input:focus ~ .placeholder,
.form-field input:not(:placeholder-shown) ~ .placeholder,
.form-field select:focus ~ .placeholder {
  top: 0;
  font-size: 0.75rem;
  color: var(--color-text-secondary);
}

.form-field input:focus,
.form-field select:focus {
  outline: none;
  border-color: var(--color-text-secondary);
}

.submit-button {
  padding: 0.938rem 1.875rem;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 7.5rem;
  background: var(--color-primary);
  color: var(--color-white);
}

.submit-button:hover {
  background: var(--color-gray-800);
}

.quick-call-button {
  background: none;
  border: none;
  color: var(--color-text-muted);
  cursor: pointer;
  text-decoration: underline;
}

.form-step {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  margin: auto 0;
}

.form-step h3 {
  margin: 0;
  font-size: 1.125rem;
  color: var(--color-text-secondary);
}

.form-navigation {
  display: flex;
  justify-content: space-between;
  gap: 0.313rem;
  margin-top: 1.25rem;
}

.nav-button {
  padding: 0.938rem 1.875rem;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 7.5rem;
  background: var(--color-gray-400);
  color: var(--color-text-primary);
}

.nav-button:hover {
  background: var(--color-gray-200);
}

.next-button {
  padding: 0.938rem 1.875rem;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 7.5rem;
  background: var(--color-primary);
  color: var(--color-white);
}

.next-button:hover {
  background: var(--color-gray-800);
}

.file-upload {
  padding: 1.25rem;
  border: 0.125rem dashed var(--color-gray-500);
  border-radius: 0.25rem;
  text-align: center;
  position: relative;
  cursor: pointer;
  transition: background-color 0.3s ease;
}

.file-upload.dragging {
  background-color: rgba(0, 0, 0, 0.1);
  border-color: var(--color-text-secondary);
}

.file-upload p {
  margin-top: 0.625rem;
  color: var(--color-text-muted);
}

@media (max-width: 30rem) {
  .modal-content {
    padding: 1.25rem;
  }

  .contact-form {
    gap: 1.25rem;
  }
}

.success-message {
  text-align: center;
  padding: 2.5rem 1.25rem;
  animation: fadeIn 0.3s ease;
}

.success-icon {
  width: 3.75rem;
  height: 3.75rem;
  background: #4caf50;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.25rem;
  color: white;
  font-size: 1.875rem;
  animation: scaleIn 0.3s ease;
}

.success-message h3 {
  font-size: 1.5rem;
  margin-bottom: 0.625rem;
  color: #121212;
}

.success-message p {
  color: #666;
  font-size: 1rem;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(1.25rem);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes scaleIn {
  from {
    transform: scale(0);
  }

  to {
    transform: scale(1);
  }
}

.header-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.25rem;
}

.close-button {
  margin-left: auto;
  display: flex;
  align-items: center;
  cursor: pointer;
}

.progress-bar {
  position: relative;
  height: 0.938rem;
  background: #f0f0f0;
  border-radius: 0.313rem;
  margin: 1.25rem;
  flex-grow: 1;
}

.progress {
  height: 100%;
  background: #000;
  border-radius: 0.313rem;
  transition: width 0.3s ease;
}

.progress-text {
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  font-size: 0.75rem;
  color: #fff;
}

.modal-body {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 25rem;
}

.form-content {
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  overflow-y: auto;
}

.quick-call-button-container {
  text-align: center;
  margin-top: 0.625rem;
}
</style>
